// Rolling scan-session stats. Measure before fixing.
//
// Ported from event-connect's scanner (telemetry.ts): records how long decodes really take (a
// distribution, not just the last value), how often they blow the frame budget, and how long the
// camera goes without seeing anything — so a slow-feeling scanner can be diagnosed instead of
// guessed at.
//
// Pure and allocation-free per sample — it must not perturb the thing it measures. Kept out of
// the loop component so it unit-tests with plain numbers, same discipline as frame-tick.ts. This
// is a ref (useScanLoop.ts), never React state: recording ~15 samples/second into state would
// re-render the scanner at decode rate.
//
// event-connect's formatSummary()/one-line dashboard string is intentionally not ported — this
// step has no telemetry dashboard surface (explicitly out of scope for this port).

import type { Rect, Size } from './decoder/types'
import { expectedPxPerModule } from './scan-geometry'

/** One rAF frame at the loop's 15Hz target. A decode longer than this cannot keep up. */
export const FRAME_BUDGET_MS = 1000 / 15

/** Cap on retained samples — bounded memory, and recent behaviour is what matters. */
const MAX_SAMPLES = 600

/** Camera-tuning context reported alongside the decode stats. camera-tuning.ts (resolution
 * ladder/zoom/focus) was not ported in this pass (see product-scan's port notes) — those fields
 * stay null here, only decodeOptionsName is ever set. */
export interface ScanTelemetryContext {
  readonly resRung: 0 | 1 | 2 | null
  readonly zoomRequested: number | null
  readonly zoomGranted: number | null
  readonly zoomCaps: { readonly min: number; readonly max: number } | null
  readonly focus: 'continuous' | 'n-a' | null
  readonly lens: string | null
  readonly decodeOptionsName: 'thorough' | 'lean' | null
}

export interface ScanTelemetrySummary {
  readonly decodes: number
  readonly p50: number
  readonly p95: number
  readonly max: number
  /** Decodes that exceeded one frame budget — the "sticky" signal. */
  readonly overBudget: number
  readonly overBudgetPct: number
  /** Detections that produced a barcode, vs decodes that found nothing. */
  readonly hits: number
  /** Longest consecutive run of decodes that found nothing. */
  readonly longestMissStreak: number
  readonly errors: number
  readonly detectRate: number
  readonly cam: Size | null
  readonly roi: Size | null
  readonly expectedPxPerModule: number | null
  /** Measured from real hits' boundingBox width — the ground truth REALISTIC_FILL is fitted
   * against. Null until the session has at least one hit. */
  readonly measuredPxPerModuleP50: number | null
  readonly context: ScanTelemetryContext
}

/** Nearest-rank percentile on an already-sorted ascending array. */
export function percentile(sorted: readonly number[], p: number): number {
  if (sorted.length === 0) return 0
  const rank = Math.ceil((p / 100) * sorted.length)
  const index = Math.min(Math.max(rank - 1, 0), sorted.length - 1)
  return sorted[index]
}

const EMPTY_CONTEXT: ScanTelemetryContext = {
  resRung: null,
  zoomRequested: null,
  zoomGranted: null,
  zoomCaps: null,
  focus: null,
  lens: null,
  decodeOptionsName: null,
}

export class ScanTelemetry {
  // TS `private`, not JS `#` private fields — Gatsby's SSR webpack/babel config doesn't have
  // @babel/plugin-transform-private-methods enabled.
  private samples: number[] = []
  private hits = 0
  private errors = 0
  private missStreak = 0
  private longestMissStreak = 0
  private startedAt: number | null = null
  private lastAt = 0
  private cam: Size | null = null
  private roi: Size | null = null
  private measuredPxPerModule: number[] = []
  private context: ScanTelemetryContext = EMPTY_CONTEXT

  /** One completed decode pass. `found` is whether it produced any detection. */
  record(durationMs: number, found: boolean, now: number): void {
    if (this.startedAt === null) this.startedAt = now
    this.lastAt = now

    // Drop the oldest rather than growing without bound; a session can run for minutes.
    if (this.samples.length >= MAX_SAMPLES) this.samples.shift()
    this.samples.push(durationMs)

    if (found) {
      this.hits += 1
      this.missStreak = 0
      return
    }
    this.missStreak += 1
    if (this.missStreak > this.longestMissStreak) this.longestMissStreak = this.missStreak
  }

  recordError(): void {
    this.errors += 1
  }

  /** Called only on capture.resized (ROI/frame changed), not per frame — cheap by construction. */
  recordGeometry(cam: Size, roi: Rect): void {
    this.cam = cam
    this.roi = { width: roi.width, height: roi.height }
  }

  /** A hit's boundingBox width, in ROI-canvas pixels — the ground truth REALISTIC_FILL is
   * fitted against, and the input the decodable-floor check prefers once it exists. */
  recordMeasuredPxPerModule(pxPerModule: number): void {
    if (this.measuredPxPerModule.length >= MAX_SAMPLES) this.measuredPxPerModule.shift()
    this.measuredPxPerModule.push(pxPerModule)
  }

  /** Camera-tuning context (resolution rung, zoom, focus, lens, decode-option set) — set
   * wholesale whenever any of it changes, never per-frame. */
  setContext(context: Partial<ScanTelemetryContext>): void {
    this.context = { ...this.context, ...context }
  }

  /** The measured px/module the decodable-floor check should use once any hit exists. */
  latestMeasuredPxPerModule(): number | null {
    const n = this.measuredPxPerModule.length
    return n === 0 ? null : this.measuredPxPerModule[n - 1]
  }

  summary(): ScanTelemetrySummary {
    const sorted = [...this.samples].sort((a, b) => a - b)
    const decodes = sorted.length
    const overBudget = sorted.filter(ms => ms > FRAME_BUDGET_MS).length
    const elapsed = this.startedAt === null ? 0 : this.lastAt - this.startedAt
    const measuredSorted = [...this.measuredPxPerModule].sort((a, b) => a - b)

    return {
      decodes,
      p50: round(percentile(sorted, 50)),
      p95: round(percentile(sorted, 95)),
      max: round(sorted[decodes - 1] ?? 0),
      overBudget,
      overBudgetPct: decodes === 0 ? 0 : round((overBudget / decodes) * 100),
      hits: this.hits,
      longestMissStreak: this.longestMissStreak,
      errors: this.errors,
      // Intervals, not samples: N decodes spaced 100ms apart span N-1 intervals, so dividing
      // by the count overstates the cadence (10 samples over 900ms is 10/s, not 11.1/s).
      detectRate: elapsed <= 0 || decodes < 2 ? 0 : round(((decodes - 1) * 1000) / elapsed),
      cam: this.cam,
      roi: this.roi,
      expectedPxPerModule: this.roi ? round(expectedPxPerModule(this.roi.width)) : null,
      measuredPxPerModuleP50: measuredSorted.length === 0 ? null : round(percentile(measuredSorted, 50)),
      context: this.context,
    }
  }
}

function round(value: number): number {
  return Math.round(value * 10) / 10
}
